import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IPersonsCollaborations } from 'aa-ng';
import { Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';
import { PersonAdditionalProperties } from 'src/app/models/person-additional-properties';

import {
  IActivityCollaborator,
  IActivityCollaboratorQuery,
  IInstitutionContext,
  IPerson,
  IPersonCollaborations,
  IPersonSummary,
  IResearchTerm,
  IUnit,
  mapPersonCollaborations,
  mapPersonSummaryToPerson,
  PersonProfile,
} from '../../models';
import { IResultSet } from '../models';
import { InstitutionsService } from './institutions.service';

@Injectable({ providedIn: 'root' })
export class PeopleService {
  private http = inject(HttpClient);
  private institutionsService = inject(InstitutionsService);

  private _baseUrl = '/api/people';
  private _personCache: { [key: number]: PersonProfile } = {};

  get(id: number): Observable<PersonProfile> {
    if (this._personCache[id]) {
      return of(this._personCache[id]);
    } else {
      return this.institutionsService.context.pipe(
        switchMap((context: IInstitutionContext) => {
          return this.http.get(`${this._baseUrl}/${id}`).pipe(
            map((person: PersonProfile) => {
              person.educationalMaterials ??= [];
              const profile: PersonProfile = Object.assign(
                new PersonProfile(),
                person,
              );
              const unitAffiliations: IUnit[] = person.unitAffiliations.filter(
                (unit) =>
                  unit.institutionId === +context.institutionId ||
                  (context.institutionGroup &&
                    context.institutionGroup.institutions
                      .map((i) => i.id)
                      .includes(unit.institutionId)),
              );
              profile.primaryUnitAffiliation = unitAffiliations.find(
                (unit) => unit.isPrimary,
              );
              profile.nonPrimaryUnitAffiliations =
                unitAffiliations.filter((unit) => !unit.isPrimary) || [];
              this._personCache[id] = profile;
              return profile;
            }),
            take(1),
          );
        }),
        take(1),
      );
    }
  }

  getSimilarScholars(id: number): Observable<IPerson[]> {
    return this.institutionsService.context.pipe(
      map((context) =>
        context.institutionGroup && context.institutionGroup.institutions ?
          context.institutionGroup.institutions.map((i) => i.id)
        : null,
      ),
      mergeMap((institutionIds) =>
        this.http
          .post<IPersonSummary[]>(`${this._baseUrl}/similar`, {
            id,
            institutionIds,
          })
          .pipe(
            map((personSummaries) =>
              personSummaries.map((personSummary) =>
                mapPersonSummaryToPerson(personSummary),
              ),
            ),
          ),
      ),
    );
  }

  getEditedResearchKeywords(id: number): Observable<IResearchTerm[]> {
    return this.http.get<IResearchTerm[]>(
      `${this._baseUrl}/getEditedResearchKeywords/${id}`,
    );
  }

  getScholarsByUnit(instId: number, unitId: number) {
    return this.http.get<IPerson[]>(
      `${this._baseUrl}/getbyunitids/${instId}/${unitId}`,
    );
  }

  getAdditonalProperties(id: number): Observable<PersonAdditionalProperties> {
    return this.http.get<PersonAdditionalProperties>(
      `${this._baseUrl}/getadditionalproperties/${id}`,
    );
  }

  /**
   * A short-lived link to the person's profile picture. The server looks up the stored file
   * itself, since it can be a full blob path that would not survive being put in the URL.
   */
  getProfilePictureUrl(id: number): Observable<string> {
    return this.http.get<string>(
      `${this._baseUrl}/download/profilepicture/${id}`,
    );
  }

  /**
   * One page of an activity's collaborators, keyed on the **activity guid**.
   *
   * Replaces a full fetch of every person on the record. The guid resolves to exactly one
   * `activity.PersonActivity` row, so no owning scholar has to be named - EDS reads only the
   * single-tenant store, so there is no per-owner grain to disambiguate.
   */
  getCollaboratorsByActivityGuidPaged(
    activityGuid: string,
    query: IActivityCollaboratorQuery = {},
  ): Observable<IResultSet<IActivityCollaborator[]>> {
    let params = new HttpParams()
      .set('pageIndex', query.pageIndex ?? 0)
      .set('pageSize', query.pageSize ?? 25)
      .set('sortDescending', query.sortDescending ?? false);

    if (query.searchTerm) {
      params = params.set('searchTerm', query.searchTerm);
    }

    if (query.sortProperty) {
      params = params.set('sortProperty', query.sortProperty);
    }

    return this.http.get<IResultSet<IActivityCollaborator[]>>(
      `${this._baseUrl}/getbyactivitypaged/${activityGuid}`,
      { params },
    );
  }
  getCollaborationsByUnit(
    unitId: number,
    institutionId: number,
    includeAllPeople: boolean,
  ): Observable<IPersonsCollaborations[]> {
    const url = `${this._baseUrl}/collaborations/unit/${unitId}/${institutionId}/${includeAllPeople}`;
    return this.http.get<IPersonCollaborations[]>(url).pipe(
      map((personCollaborations) => {
        return personCollaborations.map((collab) =>
          mapPersonCollaborations(collab),
        );
      }),
    );
  }

  getCollaborationsByPersonIds(
    personIds: number[],
    institutionIds: number[],
    includeExtended: boolean = false,
    prioritizedUnitId: number | null = null,
  ): Observable<IPersonCollaborations[]> {
    const url =
      prioritizedUnitId != null ?
        `${this._baseUrl}/collaborations-by-person-ids/${prioritizedUnitId}`
      : `${this._baseUrl}/collaborations-by-person-ids`;
    return this.http.post<IPersonCollaborations[]>(url, {
      personIds,
      institutionIds,
      includeExtended,
    });
  }
}
